import axios from "axios";
import React, { useState, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import EditableCategorySelect from "./EditableCategorySelect";
import UploadArticlesTable from "./UploadArticlesTable";
import UploadArticlesPendingRetriesTable from "./UploadArticlesPendingRetriesTable";
import StatusMessage from "./StatusMessage";
import { useUploadArticlesPublish } from "contexts/UploadArticlesPublishContext";
import {
  fetchAllSelectableArticles,
  getUploadArticleKey,
  getUploadArticlesAuthHeaders,
  isPublishableUploadArticle,
  UPLOAD_ARTICLES_LIST_QUERY_KEY,
  UPLOAD_ARTICLES_PAGE_SIZE,
  useUploadArticlesList,
} from "hooks/useUploadArticlesList";
import {
  formatBulkUploadSummary,
  parseAsinsFromFileText,
  uploadAsinsInChunks,
} from "utils/uploadArticlesBulkUpload";

const UploadArticles = () => {
  const queryClient = useQueryClient();
  const [category, setCategory] = useState(null);
  const [categoryName, setCategoryName] = useState(null);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(null);
  const [selectedArticles, setSelectedArticles] = useState(() => new Map());
  const [uploadingAsins, setUploadingAsins] = useState(false);
  const [asinUploadProgress, setAsinUploadProgress] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [tablePage, setTablePage] = useState(1);
  const [selectAllScope, setSelectAllScope] = useState("none");
  const [selectAllLoading, setSelectAllLoading] = useState(false);
  const [selectAllProgress, setSelectAllProgress] = useState(null);
  const fileInputRef = useRef(null);
  const selectAllRef = useRef(null);
  const selectAllAbortRef = useRef(null);
  const asinUploadProgressRef = useRef(null);

  const {
    publishing,
    processingArticles,
    publishJobError,
    startPublishArticles,
    registerListSync,
  } = useUploadArticlesPublish();

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(searchValue);
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [searchValue]);

  useEffect(() => {
    selectAllAbortRef.current?.abort();
    selectAllAbortRef.current = null;
    setTablePage(1);
    setSelectedArticles(new Map());
    setSelectAllScope("none");
    setSelectAllLoading(false);
    setSelectAllProgress(null);
  }, [categoryName, statusFilter, debouncedSearch]);

  const selectionFilters = {
    statusFilter,
    categoryName: category && categoryName ? categoryName : null,
    search: debouncedSearch,
  };

  const listFilters = {
    page: tablePage,
    pageSize: UPLOAD_ARTICLES_PAGE_SIZE,
    statusFilter,
    categoryName: category && categoryName ? categoryName : null,
    search: debouncedSearch,
  };

  const {
    data: articlesData,
    isLoading,
    isFetching,
    isError,
    error: queryError,
  } = useUploadArticlesList(listFilters);

  const pageArticles = articlesData?.items ?? [];
  const pagination = articlesData?.pagination ?? {
    page: tablePage,
    pageSize: UPLOAD_ARTICLES_PAGE_SIZE,
    total: 0,
    totalPages: 1,
  };
  const articleFilterCounts = articlesData?.counts ?? {
    total: 0,
    processed: 0,
    unprocessed: 0,
    price0: 0,
    prohibited: 0,
    forbiddenCategory: 0,
    forbiddenTitleChar: 0,
    titleMismatch: 0,
    totalPendingRetries: 0,
  };

  useEffect(() => {
    return registerListSync({
      markPublished: (id) => {
        queryClient.setQueriesData(
          { queryKey: [UPLOAD_ARTICLES_LIST_QUERY_KEY] },
          (old) => {
            if (!old?.items) return old;
            return {
              ...old,
              items: old.items.map((article) =>
                article.id === id ? { ...article, status: true } : article
              ),
            };
          }
        );
        setSelectedArticles((prev) => {
          const next = new Map(prev);
          next.delete(String(id));
          return next;
        });
        setSelectAllScope("none");
      },
      refreshList: () => {
        queryClient.invalidateQueries({
          queryKey: [UPLOAD_ARTICLES_LIST_QUERY_KEY],
        });
      },
      clearSelection: () => {
        selectAllAbortRef.current?.abort();
        selectAllAbortRef.current = null;
        setSelectedArticles(new Map());
        setSelectAllScope("none");
        setSelectAllLoading(false);
        setSelectAllProgress(null);
      },
    });
  }, [registerListSync, queryClient]);

  const handleSelectAll = async (e) => {
    if (selectAllLoading) return;

    if (!e.target.checked) {
      selectAllAbortRef.current?.abort();
      selectAllAbortRef.current = null;
      setSelectedArticles(new Map());
      setSelectAllScope("none");
      setSelectAllProgress(null);
      return;
    }

    const abortController = new AbortController();
    selectAllAbortRef.current = abortController;
    setSelectAllLoading(true);
    setSelectAllScope("loading");
    setSelectAllProgress(null);
    setError(null);

    try {
      const selectableArticles = await fetchAllSelectableArticles(
        selectionFilters,
        {
          signal: abortController.signal,
          onProgress: setSelectAllProgress,
        }
      );

      if (abortController.signal.aborted) return;

      const nextSelection = new Map();
      selectableArticles.forEach((article) => {
        if (!isPublishableUploadArticle(article)) return;
        const key = getUploadArticleKey(article);
        if (key) nextSelection.set(key, article);
      });

      setSelectedArticles(nextSelection);
      setSelectAllScope(nextSelection.size > 0 ? "all" : "none");

      if (nextSelection.size === 0) {
        setError("No hay artículos publicables en el filtro actual");
      }
    } catch (selectAllError) {
      if (
        selectAllError?.code === "ERR_CANCELED" ||
        selectAllError?.name === "CanceledError" ||
        selectAllError?.name === "AbortError"
      ) {
        return;
      }
      console.error("Error seleccionando todos los artículos:", selectAllError);
      setError("Error al seleccionar todos los artículos del filtro");
      setSelectAllScope("none");
    } finally {
      if (selectAllAbortRef.current === abortController) {
        selectAllAbortRef.current = null;
      }
      setSelectAllLoading(false);
      setSelectAllProgress(null);
    }
  };

  const handleSelectArticle = (article) => {
    const key = getUploadArticleKey(article);
    if (!key || !isPublishableUploadArticle(article)) return;
    setSelectAllScope("none");
    setSelectedArticles((prev) => {
      const next = new Map(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.set(key, article);
      }
      return next;
    });
  };

  const getCategoryName = (article) => {
    if (!article) return "N/A";

    if (
      article.category_name &&
      !article.category_name.match(
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
      )
    ) {
      return article.category_name;
    }

    if (categories.length > 0) {
      if (article.category_id) {
        const foundCategory = categories.find(
          (cat) =>
            String(cat.id) === String(article.category_id) ||
            String(cat.value) === String(article.category_id)
        );
        if (foundCategory) {
          return foundCategory.name || foundCategory.label || "N/A";
        }
      }

      if (article.category_name) {
        const foundCategory = categories.find(
          (cat) =>
            String(cat.id) === String(article.category_name) ||
            String(cat.value) === String(article.category_name)
        );
        if (foundCategory) {
          return foundCategory.name || foundCategory.label || "N/A";
        }
      }
    }

    return article.category_name || "N/A";
  };

  const selectableOnPage = pageArticles.filter(isPublishableUploadArticle);
  const isAllSelected = selectAllScope === "all";
  const isIndeterminate =
    !selectAllLoading &&
    selectAllScope !== "all" &&
    selectedArticles.size > 0;
  const isPendingRetriesTab = statusFilter === "pending_retries";
  const selectAllDisabled =
    selectAllLoading ||
    isPendingRetriesTab ||
    statusFilter === "processed" ||
    statusFilter === "title_mismatch" ||
    statusFilter === "forbidden_title_char" ||
    pagination.total === 0 ||
    (selectableOnPage.length === 0 && selectedArticles.size === 0);

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  useEffect(() => {
    return () => {
      selectAllAbortRef.current?.abort();
    };
  }, []);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleFileUpload = async () => {
    if (!categoryName) {
      setError("Por favor, selecciona una categoría antes de procesar");
      return;
    }

    if (!selectedFile) {
      setError("Por favor, selecciona un archivo antes de procesar");
      return;
    }

    setUploadingAsins(true);
    setAsinUploadProgress(null);
    setError(null);

    try {
      const text = await selectedFile.text();
      const asins = parseAsinsFromFileText(text);

      if (asins.length === 0) {
        setError("No se encontraron ASINs en el archivo");
        setUploadingAsins(false);
        return;
      }

      const categoryExists = categories.some(
        (cat) => cat.name === categoryName || cat.label === categoryName
      );

      if (!categoryExists) {
        setError("La categoría seleccionada no es válida");
        setUploadingAsins(false);
        return;
      }

      const uploadUrl = `${process.env.REACT_APP_BACKEND_URL}/cargar-articulos/`;
      const uploadHeaders = {
        ...getUploadArticlesAuthHeaders(),
        "Content-Type": "application/json",
      };

      const result = await uploadAsinsInChunks({
        asins,
        categoryName,
        onProgress: (progress) => {
          asinUploadProgressRef.current = progress;
          setAsinUploadProgress(progress);
        },
        postChunk: async (body) => {
          const response = await axios.post(uploadUrl, body, {
            headers: uploadHeaders,
          });
          return response.data;
        },
      });

      setTablePage(1);

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [UPLOAD_ARTICLES_LIST_QUERY_KEY],
        }),
        queryClient.invalidateQueries({ queryKey: ["status-message"] }),
      ]);

      alert(formatBulkUploadSummary(result));

      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (uploadError) {
      console.error("Error cargando ASINs:", uploadError);
      const failedProgress = asinUploadProgressRef.current;
      const chunkHint =
        failedProgress?.chunkIndex != null
          ? ` (falló en el lote ${failedProgress.chunkIndex} de ${failedProgress.totalChunks})`
          : "";
      setError(
        (uploadError.response?.data?.message ||
          uploadError.message ||
          "Error al cargar los ASINs") + chunkHint
      );
    } finally {
      asinUploadProgressRef.current = null;
      setUploadingAsins(false);
      setAsinUploadProgress(null);
    }
  };

  const handleProcessButton = () => {
    if (!categoryName) {
      setError("Por favor, selecciona una categoría antes de procesar");
      return;
    }
    if (!selectedFile) {
      setError("Por favor, selecciona un archivo antes de procesar");
      return;
    }
    handleFileUpload();
  };

  const handlePublishArticles = () => {
    if (selectedArticles.size === 0) {
      setError("Por favor, selecciona al menos un artículo para publicar");
      return;
    }
    if (publishing) return;
    setError(null);
    startPublishArticles(Array.from(selectedArticles.values()));
  };

  const handleLoadedTotalArticles = () => {
    setStatusFilter(null);
  };

  const handleFilterProcessed = () => {
    setStatusFilter("processed");
  };

  const handleFilterUnprocessed = () => {
    setStatusFilter("unprocessed");
  };

  const handleFilterPrice0 = () => {
    setStatusFilter("price0");
  };

  const handleFilterProhibited = () => {
    setStatusFilter("prohibited");
  };

  const handleFilterForbiddenCategory = () => {
    setStatusFilter("forbidden_category");
  };

  const handleFilterTitleMismatch = () => {
    setStatusFilter("title_mismatch");
  };

  const handleFilterForbiddenTitleChar = () => {
    setStatusFilter("forbidden_title_char");
  };

  const handleFilterTotalPendingRetries = () => {
    setStatusFilter("pending_retries");
  };

  const handleSearchArticles = (e) => {
    setSearchValue(e.target.value);
  };

  const listErrorMessage =
    error ||
    publishJobError ||
    (isError
      ? queryError?.message || "Error al cargar los artículos"
      : null);

  const emptyListMessage = isPendingRetriesTab
    ? category
      ? "No hay reintentos pendientes para la categoría seleccionada"
      : "No hay reintentos pendientes"
    : category
      ? "No hay artículos cargados para la categoría seleccionada"
      : "No hay artículos cargados";

  return (
    <>
      <div className="bg-white rounded-lg shadow">
        <div className="px-4 py-4 flex  border-b border-gray-200">
          <div className="w-full mx-auto p-6 font-sans">
            <div className="flex flex-col md:flex-row items-center gap-4 justify-between">
              <div className="flex items-center gap-2">
                <p>Seleccioná una categoría:</p>
                <EditableCategorySelect
                  value={category}
                  onChange={(categoryId) => {
                    setCategory(categoryId);
                    const selectedCategory = categories.find(
                      (cat) =>
                        String(cat.id || cat.value) === String(categoryId)
                    );
                    if (selectedCategory) {
                      setCategoryName(
                        selectedCategory.name || selectedCategory.label
                      );
                    } else {
                      setCategoryName(null);
                    }
                  }}
                  onCategoriesChange={(cats) => {
                    setCategories(cats);
                    if (category) {
                      const selectedCategory = cats.find(
                        (cat) =>
                          String(cat.id || cat.value) === String(category)
                      );
                      if (selectedCategory) {
                        setCategoryName(
                          selectedCategory.name || selectedCategory.label
                        );
                      }
                    }
                  }}
                />
                <button
                  onClick={handleProcessButton}
                  disabled={uploadingAsins || !selectedFile || !categoryName}
                  className={`bg-primary-700 text-white px-6 py-2 rounded-lg ${
                    uploadingAsins || !selectedFile || !categoryName
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-primary-800"
                  }`}
                  id="process-articles-button"
                >
                  {uploadingAsins ? "Procesando..." : "Procesar"}
                </button>
              </div>
              <div>
                <StatusMessage />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Buscar..."
                  id="search-article-input"
                  className="border border-gray-300 rounded-md p-2"
                  value={searchValue}
                  onChange={handleSearchArticles}
                />
              </div>
            </div>

            <div className="col-span-full">
              <div
                className={`mt-2 flex justify-center rounded-lg border border-dashed px-6 py-10 transition-colors ${
                  uploadingAsins
                    ? "border-blue-400 bg-blue-50"
                    : "border-gray-900/25 hover:border-gray-400"
                }`}
              >
                <div className="text-center">
                  {uploadingAsins ? (
                    <div className="flex flex-col items-center">
                      <svg
                        className="animate-spin h-8 w-8 text-blue-600 mb-4"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      <p className="text-sm font-medium text-blue-600">
                        {asinUploadProgress
                          ? `Subiendo lote ${asinUploadProgress.chunkIndex} de ${asinUploadProgress.totalChunks} (${asinUploadProgress.percent}%)`
                          : "Preparando carga..."}
                      </p>
                      {asinUploadProgress && (
                        <div className="mt-3 w-full max-w-md">
                          <div className="h-2 overflow-hidden rounded-full bg-blue-100">
                            <div
                              className="h-full rounded-full bg-blue-600 transition-all duration-300"
                              style={{
                                width: `${asinUploadProgress.percent}%`,
                              }}
                            />
                          </div>
                          <p className="mt-2 text-xs text-gray-600">
                            {asinUploadProgress.inserted.toLocaleString("es-AR")}{" "}
                            encolados hasta ahora
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400"
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 48 48"
                        aria-hidden="true"
                      >
                        <path
                          d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <div className="mt-4 flex text-sm leading-6 text-gray-600">
                        <label
                          htmlFor="file-upload"
                          className="relative cursor-pointer rounded-md bg-white font-semibold text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 hover:text-indigo-500"
                        >
                          <span>
                            {selectedFile
                              ? selectedFile.name
                              : "Cargar archivo con ASINs"}
                          </span>
                          <input
                            id="file-upload"
                            name="file-upload"
                            type="file"
                            ref={fileInputRef}
                            className="sr-only"
                            accept=".csv,.txt"
                            onChange={handleFileSelect}
                            disabled={uploadingAsins}
                          />
                        </label>
                      </div>
                      <p className="text-xs leading-5 text-gray-600">
                        {selectedFile
                          ? "Archivo seleccionado. Haz clic en 'Procesar' para cargar los ASINs"
                          : "CSV o TXT (se envía en lotes de hasta 10.000 ASINs)"}
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className={`p-6 h-[90vh] overflow-y-auto ${
            selectedArticles.size > 0 ? "pb-28 sm:pb-24" : ""
          }`}
        >
          <div className="mb-6 rounded-2xl border border-gray-100 bg-gradient-to-br from-gray-50/80 to-white p-4 shadow-sm sm:p-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0">
                <h2 className="text-lg font-semibold tracking-tight text-gray-900">
                  Vista de artículos
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  Filtrá por estado. Los números respetan categoría y búsqueda por
                  ASIN. El checkbox de la tabla selecciona todos los artículos
                  publicables del filtro activo.
                </p>
              </div>
              {isFetching && !isLoading && (
                <span className="text-sm text-gray-500">Actualizando…</span>
              )}
            </div>

            <div
              className="mt-5 flex flex-wrap gap-2"
              role="tablist"
              aria-label="Filtrar artículos por estado"
            >
              <button
                type="button"
                role="tab"
                aria-selected={statusFilter === null}
                onClick={handleLoadedTotalArticles}
                className={`inline-flex min-w-[10rem] flex-1 flex-col items-start rounded-xl border px-4 py-3 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 sm:min-w-[8.5rem] sm:flex-none ${
                  statusFilter === null
                    ? "border-primary-500 bg-primary-50 shadow-md ring-1 ring-primary-500/25"
                    : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50/80 hover:shadow-sm"
                }`}
              >
                <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Todos
                </span>
                <span className="mt-1 flex w-full items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-gray-900">
                    Cargados
                  </span>
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-xs font-bold tabular-nums ${
                      statusFilter === null
                        ? "bg-primary-600 text-white"
                        : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    {articleFilterCounts.total}
                  </span>
                </span>
              </button>

              <button
                type="button"
                role="tab"
                aria-selected={statusFilter === "processed"}
                onClick={handleFilterProcessed}
                className={`inline-flex min-w-[10rem] flex-1 flex-col items-start rounded-xl border px-4 py-3 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 sm:min-w-[8.5rem] sm:flex-none ${
                  statusFilter === "processed"
                    ? "border-green-600 bg-green-50 shadow-md ring-1 ring-green-600/25"
                    : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50/80 hover:shadow-sm"
                }`}
              >
                <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Estado
                </span>
                <span className="mt-1 flex w-full items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-gray-900">
                    Procesados
                  </span>
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-xs font-bold tabular-nums ${
                      statusFilter === "processed"
                        ? "bg-green-600 text-white"
                        : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    {articleFilterCounts.processed}
                  </span>
                </span>
              </button>

              <button
                type="button"
                role="tab"
                aria-selected={statusFilter === "unprocessed"}
                onClick={handleFilterUnprocessed}
                className={`inline-flex min-w-[10rem] flex-1 flex-col items-start rounded-xl border px-4 py-3 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 sm:min-w-[8.5rem] sm:flex-none ${
                  statusFilter === "unprocessed"
                    ? "border-orange-500 bg-orange-100 shadow-md ring-1 ring-orange-500/25"
                    : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50/80 hover:shadow-sm"
                }`}
              >
                <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Pendientes
                </span>
                <span className="mt-1 flex w-full items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-gray-900">
                    Sin procesar
                  </span>
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-xs font-bold tabular-nums ${
                      statusFilter === "unprocessed"
                        ? "bg-orange-500 text-white"
                        : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    {articleFilterCounts.unprocessed}
                  </span>
                </span>
              </button>

              <button
                type="button"
                role="tab"
                aria-selected={statusFilter === "price0"}
                onClick={handleFilterPrice0}
                className={`inline-flex min-w-[10rem] flex-1 flex-col items-start rounded-xl border px-4 py-3 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 sm:min-w-[8.5rem] sm:flex-none ${
                  statusFilter === "price0"
                    ? "border-yellow-500 bg-yellow-50 shadow-md ring-1 ring-yellow-500/30"
                    : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50/80 hover:shadow-sm"
                }`}
              >
                <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Precio
                </span>
                <span className="mt-1 flex w-full items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-gray-900">
                    Precio cero
                  </span>
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-xs font-bold tabular-nums ${
                      statusFilter === "price0"
                        ? "bg-yellow-500 text-white"
                        : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    {articleFilterCounts.price0}
                  </span>
                </span>
              </button>

              <button
                type="button"
                role="tab"
                aria-selected={statusFilter === "prohibited"}
                onClick={handleFilterProhibited}
                className={`inline-flex min-w-[10rem] flex-1 flex-col items-start rounded-xl border px-4 py-3 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 sm:min-w-[8.5rem] sm:flex-none ${
                  statusFilter === "prohibited"
                    ? "border-red-500 bg-red-200 shadow-md ring-1 ring-red-500/25"
                    : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50/80 hover:shadow-sm"
                }`}
              >
                <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Compliance
                </span>
                <span className="mt-1 flex w-full items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-gray-900">
                    Marcas prohibidas
                  </span>
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-xs font-bold tabular-nums ${
                      statusFilter === "prohibited"
                        ? "bg-red-500 text-white"
                        : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    {articleFilterCounts.prohibited}
                  </span>
                </span>
              </button>

              <button
                type="button"
                role="tab"
                aria-selected={statusFilter === "forbidden_category"}
                onClick={handleFilterForbiddenCategory}
                className={`inline-flex min-w-[10rem] flex-1 flex-col items-start rounded-xl border px-4 py-3 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 sm:min-w-[8.5rem] sm:flex-none ${
                  statusFilter === "forbidden_category"
                    ? "border-red-500 bg-red-200 shadow-md ring-1 ring-red-500/25"
                    : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50/80 hover:shadow-sm"
                }`}
              >
                <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Compliance
                </span>
                <span className="mt-1 flex w-full items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-gray-900">
                    Categoría prohibida
                  </span>
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-xs font-bold tabular-nums ${
                      statusFilter === "forbidden_category"
                        ? "bg-red-500 text-white"
                        : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    {articleFilterCounts.forbiddenCategory}
                  </span>
                </span>
              </button>

              <button
                type="button"
                role="tab"
                aria-selected={statusFilter === "forbidden_title_char"}
                onClick={handleFilterForbiddenTitleChar}
                className={`inline-flex min-w-[10rem] flex-1 flex-col items-start rounded-xl border px-4 py-3 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 sm:min-w-[8.5rem] sm:flex-none ${
                  statusFilter === "forbidden_title_char"
                    ? "border-red-500 bg-red-200 shadow-md ring-1 ring-red-500/25"
                    : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50/80 hover:shadow-sm"
                }`}
              >
                <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Compliance
                </span>
                <span className="mt-1 flex w-full items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-gray-900">
                    Carácter prohibido en título
                  </span>
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-xs font-bold tabular-nums ${
                      statusFilter === "forbidden_title_char"
                        ? "bg-red-500 text-white"
                        : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    {articleFilterCounts.forbiddenTitleChar}
                  </span>
                </span>
              </button>

              <button
                type="button"
                role="tab"
                aria-selected={statusFilter === "title_mismatch"}
                onClick={handleFilterTitleMismatch}
                className={`inline-flex min-w-[10rem] flex-1 flex-col items-start rounded-xl border px-4 py-3 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 sm:min-w-[8.5rem] sm:flex-none ${
                  statusFilter === "title_mismatch"
                    ? "border-red-500 bg-red-200 shadow-md ring-1 ring-red-500/25"
                    : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50/80 hover:shadow-sm"
                }`}
              >
                <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Compliance
                </span>
                <span className="mt-1 flex w-full items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-gray-900">
                    Títulos no coinciden
                  </span>
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-xs font-bold tabular-nums ${
                      statusFilter === "title_mismatch"
                        ? "bg-red-500 text-white"
                        : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    {articleFilterCounts.titleMismatch}
                  </span>
                </span>
              </button>

              <button
                type="button"
                role="tab"
                aria-selected={statusFilter === "pending_retries"}
                onClick={handleFilterTotalPendingRetries}
                className={`inline-flex min-w-[10rem] flex-1 flex-col items-start rounded-xl border px-4 py-3 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 sm:min-w-[8.5rem] sm:flex-none ${
                  statusFilter === "pending_retries"
                    ? "border-red-500 bg-red-200 shadow-md ring-1 ring-red-500/25"
                    : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50/80 hover:shadow-sm"
                }`}
              >
                <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Compliance
                </span>
                <span className="mt-1 flex w-full items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-gray-900">
                    Pendientes (retries fallidos)
                  </span>
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-xs font-bold tabular-nums ${
                      statusFilter === "pending_retries"
                        ? "bg-red-500 text-white"
                        : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    {articleFilterCounts.totalPendingRetries}
                  </span>
                </span>
              </button>
            </div>
          </div>

          {listErrorMessage && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
              {listErrorMessage}
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-16 text-gray-500">
              <svg
                className="animate-spin h-8 w-8 text-primary-600 mr-3"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Cargando artículos…
            </div>
          ) : pageArticles.length === 0 ? (
            <p className="text-gray-500">{emptyListMessage}</p>
          ) : isPendingRetriesTab ? (
            <UploadArticlesPendingRetriesTable
              items={pageArticles}
              pagination={{
                currentPage: pagination.page,
                pageSize: pagination.pageSize,
                totalItems: pagination.total,
                onPageChange: setTablePage,
              }}
            />
          ) : (
            <UploadArticlesTable
              loadedArticles={pageArticles}
              selectedArticles={selectedArticles}
              getArticleKey={getUploadArticleKey}
              isArticleSelectable={isPublishableUploadArticle}
              handleSelectAll={handleSelectAll}
              handleSelectArticle={handleSelectArticle}
              getCategoryName={getCategoryName}
              selectAllRef={selectAllRef}
              isAllSelected={isAllSelected}
              isIndeterminate={isIndeterminate}
              selectAllDisabled={selectAllDisabled}
              selectAllLoading={selectAllLoading}
              processingArticles={processingArticles}
              pagination={{
                currentPage: pagination.page,
                pageSize: pagination.pageSize,
                totalItems: pagination.total,
                onPageChange: setTablePage,
              }}
            />
          )}
        </div>
      </div>

      {!isPendingRetriesTab &&
        (selectedArticles.size > 0 || selectAllLoading) && (
        <div
          className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white/95 px-4 py-3 shadow-[0_-8px_24px_rgba(0,0,0,0.08)] backdrop-blur-md"
          style={{
            paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))",
          }}
        >
          <div className="mx-auto flex max-w-6xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-center text-sm text-gray-700 sm:text-left">
              {selectAllLoading ? (
                <>
                  Seleccionando artículos…{" "}
                  {selectAllProgress?.total
                    ? `(${selectAllProgress.loaded.toLocaleString()} de ${selectAllProgress.total.toLocaleString()})`
                    : selectAllProgress?.loaded
                      ? `(${selectAllProgress.loaded.toLocaleString()} listos)`
                      : ""}
                </>
              ) : (
                <>
                  <span className="font-semibold text-gray-900">
                    {selectedArticles.size.toLocaleString()}
                  </span>{" "}
                  artículo(s) seleccionado(s) para publicar
                  {selectAllScope === "all" ? " (todos los del filtro)" : ""}
                </>
              )}
            </p>
            <button
              type="button"
              onClick={handlePublishArticles}
              disabled={publishing || selectAllLoading || selectedArticles.size === 0}
              className="inline-flex w-full shrink-0 items-center justify-center rounded-xl bg-green-600 px-6 py-3 text-sm font-semibold text-white shadow-md transition hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:py-2.5"
            >
              {publishing
                ? "Publicando…"
                : `Publicar ${selectedArticles.size}`}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default UploadArticles;
